import type { EmojiMartData } from '@emoji-mart/data';

// emoji-mart and its data load on demand, never in the main bundle. One
// module-level promise, no retry: a failed load leaves the text as typed.
let emojiData: Promise<EmojiMartData> | null = null;

export function loadEmojiData(): Promise<EmojiMartData> {
  emojiData ??= (async () => {
    const data = (await import('@emoji-mart/data'))
      .default as unknown as EmojiMartData;
    const { init } = await import('emoji-mart');
    await init({ data });
    return data;
  })();
  return emojiData;
}

const QUERY_BEFORE_CARET = /(^|\s):([a-z0-9_+-]{2,})$/i;
const TYPED_SHORTCODE = /(^|\s):([a-z0-9_+-]+):$/i;

/** The `:query` that ends at the caret, or null when there is none. */
export function findShortcodeQuery(
  value: string,
  caret: number
): { start: number; query: string } | null {
  const match = QUERY_BEFORE_CARET.exec(value.slice(0, caret));
  if (!match) return null;
  return {
    start: match.index + match[1].length,
    query: match[2].toLowerCase(),
  };
}

export async function searchShortcodes(
  query: string,
  limit: number
): Promise<{ id: string; native: string }[]> {
  await loadEmojiData();
  const { SearchIndex } = await import('emoji-mart');
  const results: { id: string; skins: { native: string }[] }[] =
    await SearchIndex.search(query);
  return (results ?? []).slice(0, limit).map(r => ({
    id: r.id,
    native: r.skins[0].native,
  }));
}

export function replaceRange(
  value: string,
  start: number,
  end: number,
  insert: string
): { value: string; caret: number } {
  return {
    value: value.slice(0, start) + insert + value.slice(end),
    caret: start + insert.length,
  };
}

/**
 * Turns a `:name:` that the user just closed into its emoji. Only a single
 * typed `:` converts, so a deletion or a paste never does.
 */
export function convertTypedShortcode(
  data: EmojiMartData,
  prev: string,
  next: string,
  caret: number
): { value: string; caret: number } | null {
  if (next.length !== prev.length + 1 || next[caret - 1] !== ':') return null;
  const match = TYPED_SHORTCODE.exec(next.slice(0, caret));
  if (!match) return null;
  const name = match[2].toLowerCase();
  // Own keys only: ':constructor:' and ':__proto__:' must stay plain text.
  const has = (o: object, k: string) =>
    Object.prototype.hasOwnProperty.call(o, k);
  const key = has(data.emojis, name)
    ? name
    : has(data.aliases, name)
      ? data.aliases[name]
      : null;
  const emoji = key !== null && has(data.emojis, key) ? data.emojis[key] : null;
  if (!emoji) return null;
  return replaceRange(
    next,
    match.index + match[1].length,
    caret,
    emoji.skins[0].native
  );
}
